import { DIAGRAM_MAX_ELEMENTS, type DiagramElement, type DiagramPitch, type TrainingFormationDto } from '@ukinfo/shared';
import { useState } from 'react';
import { Button, Checkbox, Dialog, EmptyState, ErrorBlock, LoadingBlock, cn } from '../ui';
import { GameSizeFilterBar, FormationSummary, useFormationList, type GameSizeFilter } from './FormationList';
import { formationToElements, type FormationInsertOptions } from './formationToElements';
import { PitchDiagram } from './PitchDiagram';

/**
 * Auswahl einer Formation zum Einfuegen in eine Skizze (aus dem Skizzeneditor, Gruppe "Einfuegen" -> "Formation").
 *
 * Spielform filtern, Formation waehlen, Mannschaft A oder B, "gespiegelt" fuer die Gegnerseite und bei
 * ganzem Feld "nur eine Haelfte". Die Vorschau zeigt das Ergebnis auf dem Feld der Skizze, bevor
 * eingefuegt wird. Der Editor haengt die Spieler selbst an (ein Rueckgaengig-Schritt, Grenze 80).
 *
 * Wird nur gemountet, solange es offen ist: der Editor laeuft damit auch ohne Abfrage-Zusammenhang.
 */
export function FormationPicker({
  pitch,
  used,
  onInsert,
  onClose,
}: {
  /** Feld der Skizze. */
  pitch: DiagramPitch;
  /** Zahl der Elemente, die die Skizze schon hat. */
  used: number;
  onInsert: (elements: DiagramElement[]) => void;
  onClose: () => void;
}): JSX.Element {
  const [gameSize, setGameSize] = useState<GameSizeFilter>('ALL');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [team, setTeam] = useState<FormationInsertOptions['team']>('A');
  const [mirrored, setMirrored] = useState(false);
  const [half, setHalf] = useState(false);
  const list = useFormationList(gameSize);

  const selected: TrainingFormationDto | undefined = list.data?.find((formation) => formation.id === selectedId);
  const elements = selected ? formationToElements(selected, { team, mirrored, pitch, half }) : [];
  const free = DIAGRAM_MAX_ELEMENTS - used;
  const tooMany = selected !== undefined && elements.length > free;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Formation einfügen"
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button disabled={!selected || tooMany} onClick={() => onInsert(elements)}>Einfügen</Button>
        </>
      }
    >
      <div className="space-y-4">
        <GameSizeFilterBar value={gameSize} onChange={setGameSize} />

        {list.isLoading && <LoadingBlock />}
        {list.error && <ErrorBlock error={list.error} />}
        {list.data && list.data.length === 0 && <EmptyState title="Keine Formationen gefunden" description="Wähle eine andere Spielform." />}
        {list.data && list.data.length > 0 && (
          <ul aria-label="Formationen" className="grid max-h-72 grid-cols-1 gap-2 overflow-y-auto pr-1 min-[420px]:grid-cols-2">
            {list.data.map((formation) => (
              <li key={formation.id}>
                <button
                  type="button"
                  aria-pressed={formation.id === selectedId}
                  aria-label={`${formation.name} (${formation.gameSize}er${formation.source === 'CATALOG' ? ', Standard' : ''})`}
                  onClick={() => setSelectedId(formation.id)}
                  className={cn(
                    'flex h-full w-full gap-3 rounded-2xl border p-2 text-left shadow-sm transition-colors',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60',
                    formation.id === selectedId ? 'border-brand-600 bg-brand-50 ring-1 ring-brand-600' : 'border-slate-200 bg-white hover:border-brand-300',
                  )}
                >
                  <FormationSummary formation={formation} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <fieldset className="space-y-3 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200">
          <legend className="px-1 text-sm font-medium text-slate-700">So wird eingefügt</legend>
          <div role="group" aria-label="Mannschaft" className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-slate-700">Mannschaft</span>
            {(['A', 'B'] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={team === value}
                aria-label={`Team ${value}`}
                onClick={() => setTeam(value)}
                className={cn(
                  'min-h-11 min-w-14 rounded-xl border px-3 text-sm font-medium shadow-sm transition-colors sm:min-h-0 sm:py-1.5',
                  team === value ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
                )}
              >
                Team {value}
              </button>
            ))}
          </div>
          <Checkbox label="Gespiegelt (Gegnerseite: Torwart oben, Spielrichtung nach unten)" checked={mirrored} onChange={setMirrored} />
          {pitch === 'FULL' && <Checkbox label="Nur eine Hälfte (die eigene, bei „gespiegelt“ die obere)" checked={half} onChange={setHalf} />}
          {pitch === 'HALF' && <p className="text-xs text-slate-500">Auf dem halben Feld wird die Formation auf die Hälfte umgerechnet; das Tor liegt oben.</p>}
          {pitch === 'AREA' && <p className="text-xs text-slate-500">Auf der freien Fläche werden die Positionen anteilig übernommen.</p>}
          <p className="text-xs text-slate-500">Der Torwart wird als neutraler Spieler „TW“ eingefügt, alle anderen als gewähltes Team.</p>
        </fieldset>

        {selected && (
          <div className="mx-auto max-w-xs sm:max-w-sm">
            <p className="mb-1 text-sm font-medium text-slate-700">Vorschau</p>
            <PitchDiagram diagram={{ pitch, elements }} title={`Vorschau ${selected.name}`} className="max-h-[50vh] rounded-lg" />
            <p className={cn('mt-2 text-xs', tooMany ? 'font-medium text-amber-700' : 'text-slate-500')} role="status">
              {tooMany
                ? `Es sind nur noch ${Math.max(free, 0)} Plätze frei – die Formation hat ${elements.length} Spieler. Lösche zuerst ein paar Elemente der Skizze.`
                : `${elements.length} Spieler werden eingefügt (noch ${free} Plätze frei).`}
            </p>
          </div>
        )}
      </div>
    </Dialog>
  );
}
